import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import config from '../config.json';
import { useParams } from "react-router-dom";
import { CircularProgress } from '@mui/material';
import { BsCheckLg, BsEnvelope, BsExclamationLg } from "react-icons/bs";
import styled from 'styled-components';
import MSLogo from '../../assets/images/headerLogo/formjunctionlogo.png';

const PageWrapper = styled.div`
    min-height: 100vh;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px 16px;
    background: linear-gradient(135deg, #fff7ec 0%, #f5f6fa 55%, #eef0f6 100%);
    font-family: "Poppins", "Roboto", sans-serif;

    .verify-card {
        position: relative;
        width: 100%;
        max-width: 440px;
        background: #ffffff;
        border-radius: 14px;
        overflow: hidden;
        box-shadow: 0 10px 40px rgba(8, 23, 53, 0.08);
        padding: 44px 36px 40px;
        text-align: center;
    }

    .top-line {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 8px;
        background-color: #f7941d;
    }

    .bottom-line {
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 5px;
        background-color: #f7941d;
    }

    .logo {
        height: 64px;
        width: auto;
        object-fit: contain;
        margin-bottom: 28px;
    }

    .status-icon {
        width: 72px;
        height: 72px;
        margin: 0 auto 20px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 36px;
    }

    .status-icon.success {
        background-color: #fff3e3;
        color: #f7941d;
        box-shadow: 0 0 0 8px #fffaf3;
    }

    .status-icon.error {
        background-color: #fdecec;
        color: #d64545;
        box-shadow: 0 0 0 8px #fef6f6;
    }

    .status-icon.loading {
        background-color: transparent;
    }

    h1 {
        font-size: 22px;
        line-height: 1.35;
        font-weight: 600;
        color: #081735;
        margin: 0 0 10px;
    }

    p.subtitle {
        font-size: 15px;
        color: #8f95b2;
        margin: 0 0 8px;
    }

    .resend {
        margin-top: 24px;
        text-align: left;
    }

    .resend label {
        display: block;
        font-size: 14px;
        font-weight: 500;
        color: #081735;
        margin: 0 0 6px;
    }

    .input-box {
        width: 100%;
        display: flex;
        align-items: center;
        gap: 12px;
        border: 1px solid #d8dae5;
        padding: 10px 14px;
        border-radius: 6.5px;
        transition: 0.35s;
    }

    .input-box:hover {
        border-color: #919191;
    }

    .input-box:focus-within {
        border-color: #fc9202;
    }

    .input-box svg {
        font-size: 18px;
        color: #8f95b2;
        transition: 0.35s;
        flex-shrink: 0;
    }

    .input-box:focus-within svg {
        color: #fc9202;
    }

    .input-box input {
        width: 100%;
        border: 0;
        outline: 0;
        color: #081735;
        font-size: 15px;
        font-family: inherit;
        background: transparent;
    }

    .input-box input::placeholder {
        color: #8f95b2;
    }

    .primary-btn {
        width: 100%;
        margin-top: 16px;
        background-color: #fc9202;
        padding: 11px;
        color: #fff;
        border-radius: 6.5px;
        font-size: 15px;
        font-weight: 500;
        font-family: inherit;
        border: 0;
        outline: 0;
        cursor: pointer;
        transition: 0.35s;
    }

    .primary-btn:hover:not(:disabled) {
        box-shadow: 0 5px 30px 1px rgba(0, 0, 0, 0.12);
    }

    .primary-btn:active:not(:disabled) {
        scale: 0.975;
    }

    .primary-btn:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    .message {
        margin-top: 14px;
        padding: 10px 14px;
        border-radius: 6.5px;
        font-size: 14px;
        text-align: center;
    }

    .message.success {
        background-color: #fff3e3;
        color: #a35c00;
        border: 1px solid #fcd9a8;
    }

    .message.error {
        background-color: #fdecec;
        color: #b03434;
        border: 1px solid #f5c2c2;
    }

    .footer-note {
        margin-top: 28px;
        font-size: 12px;
        color: #b0b4c8;
    }
`;

export default function VerifyEmail() {
    const { verificationkey } = useParams();
    const [status, setStatus] = useState('LOADING');
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState(null);
    const [sending, setSending] = useState(false);
    const calledRef = useRef(false);

    useEffect(() => {
        if (calledRef.current) { return; }
        calledRef.current = true;

        const RAFTCO_URL = config.API_URL + "/api/employee/verifyemail/" + verificationkey;
        axios.get(RAFTCO_URL)
            .then((res) => {
                if (res.data.successCode === -1) {
                    setStatus('ERROR');
                } else {
                    setStatus(res.data.data);
                }
            }).catch(() => {
                setStatus('ERROR');
            });
    }, [verificationkey]);

    const showMessage = (type, text) => {
        setMessage({ type, text });
        setTimeout(() => { setMessage(null) }, 5000)
    }

    const resendVerification = () => {
        if (!email) { return; }
        setSending(true);
        const RAFTCO_EMAIL_URL = config.API_URL + "/api/employee/sendemailverification/" + email;
        axios.get(RAFTCO_EMAIL_URL)
            .then((res) => {
                if (res.data.successCode === 0 && res.data.data === 'SENT') {
                    showMessage('success', 'Verification email sent successfully!');
                } else if (res.data.successCode === 0 && res.data.data === 'ALREADY_VERIFIED') {
                    showMessage('success', 'This email is already verified and good to use.');
                } else if (res.data.successCode === 0 && res.data.data === 'ALREADY_SENT') {
                    showMessage('error', 'A verification email was already sent. Please check your inbox, or try again after 24 hours.');
                } else {
                    showMessage('error', 'An error occured, try again later.');
                }
            }).catch(() => {
                showMessage('error', 'An error occured, try again later.');
            }).finally(() => {
                setSending(false);
            })
    }

    const renderContent = () => {
        if (status === 'LOADING') {
            return (
                <>
                    <div className="status-icon loading">
                        <CircularProgress size={48} thickness={4} sx={{ color: '#f7941d' }} />
                    </div>
                    <h1>Verifying your email...</h1>
                    <p className="subtitle">This will only take a moment.</p>
                </>
            )
        }

        if (status === 'VALID' || status === 'ALREADY_VERIFIED') {
            return (
                <>
                    <div className="status-icon success"><BsCheckLg /></div>
                    <h1>
                        {status === 'VALID' ? 'Your email has been verified!' : 'This email is already verified and good to use.'}
                    </h1>
                    <p className="subtitle">You can close this page now.</p>
                </>
            )
        }

        return (
            <>
                <div className="status-icon error"><BsExclamationLg /></div>
                <h1>
                    {status === 'EXPIRED' ? 'Sorry, your verification link expired!' : (status === 'INVALID' ? 'Sorry, your verification link is invalid!' : 'An error occured, try again later.')}
                </h1>
                <p className="subtitle">We'll need to re-send your verification email.</p>
                <div className="resend">
                    <label htmlFor="resend-email">Email Address</label>
                    <div className="input-box">
                        <BsEnvelope />
                        <input
                            id="resend-email"
                            type="email"
                            placeholder="user@domain.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { resendVerification(); } }}
                        />
                    </div>
                    <button className="primary-btn" onClick={() => resendVerification()} disabled={!email || sending}>
                        {sending ? 'Sending...' : 'Resend Email'}
                    </button>
                    {message && <div className={`message ${message.type}`} role="alert">{message.text}</div>}
                </div>
            </>
        )
    }

    return (
        <PageWrapper>
            <div className="verify-card">
                <div className="top-line"></div>
                <img className="logo" src={MSLogo} alt="SafetySheets" />
                {renderContent()}
                <div className="footer-note">&copy; {new Date().getFullYear()} SafetySheets</div>
                <div className="bottom-line"></div>
            </div>
        </PageWrapper>
    )
}
